import type {
  SearchTicketsApiArg,
  SearchTicketsApiResponse,
} from '~/api/k2/typedefs.autogen';

import { endpoints as autogenEnhancements } from '~/api/k2/enhancements.autogen';
import { type TagName, Tags } from '~/api/k2/tags.autogen';

import { createQueryFn, withOptions } from '../utils';
import {
  downloadTicketDocument,
  DownloadTicketDocumentArg,
} from './methods/downloadTicketDocument';
import {
  downloadWorkflowDocument,
  DownloadWorkflowDocumentArg,
} from './methods/downloadWorkflowDocument';
import { LIST_TAG_ID, ticketScopedTagId } from './realtimeTags';
import { k2ApiRoot } from './root';
import {
  onTicketK2CacheEntryAdded,
  onWorkflowK2CacheEntryAdded,
} from './subscriptions';

// Apply schema-derived providesTags / invalidatesTags for all annotated endpoints.
k2ApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });
export type GetExpressionAstSchemaApiResponse = Record<string, unknown>;
export type GetExpressionAstSchemaApiArg = void;

/**
 * Provide both the arg-agnostic `LIST` tag and a ticket-scoped one.
 *
 * The schema-derived `providesTags` for the ticket list endpoints is
 * arg-agnostic (`id: 'LIST'`) even though every row belongs to one ticket, so a
 * realtime `Task/LIST` event for one ticket would refetch every cached task list
 * across every ticket. `subscriptions.ts` targets the scoped tag instead. `LIST`
 * is kept alongside it so the schema-derived mutation `invalidatesTags` — which
 * still reference it — keep working unchanged.
 */
const provideTicketScopedListTags =
  (type: TagName) =>
  (_result: unknown, _error: unknown, arg: { ticketId: string }) => [
    { type, id: LIST_TAG_ID },
    { type, id: ticketScopedTagId(arg.ticketId) },
  ];

// Realtime cache invalidation for the ticket and workflow detail views is driven
// by Pusher subscriptions (see ./subscriptions). Which tags a mutation
// invalidates comes entirely from the backend-generated `x-rtk-cache`
// annotations applied via `autogenEnhancements` above — the previous manual
// `invalidatesTags` overrides were temporary stopgaps for gaps the backend now
// covers and have been removed. The `providesTags` overrides below are a
// different thing: they widen what a query provides so realtime invalidation can
// be scoped, and leave every `invalidatesTags` untouched.
k2ApiRoot.enhanceEndpoints({
  endpoints: {
    getTicket: {
      onCacheEntryAdded: onTicketK2CacheEntryAdded,
    },
    getWorkflow: {
      onCacheEntryAdded: onWorkflowK2CacheEntryAdded,
    },
    listStages: { providesTags: provideTicketScopedListTags(Tags.Stage) },
    listTasks: { providesTags: provideTicketScopedListTags(Tags.Task) },
    listComments: { providesTags: provideTicketScopedListTags(Tags.Comment) },
    listThreads: { providesTags: provideTicketScopedListTags(Tags.Comment) },
  },
});

k2ApiRoot.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Query wrapper around the `searchTickets` POST endpoint (`/api/v1/tickets/search`).
     * Used for the tickets table in all cases (including the default unfiltered view).
     * `providesTags` matches `listTickets` so ticket mutations that invalidate the list tag
     * still refresh this query. Default `keepUnusedDataFor` retains cached responses for
     * recently used argument sets instead of refetching on every navigation.
     */
    searchTicketsList: build.query<
      SearchTicketsApiResponse,
      SearchTicketsApiArg
    >({
      query: (queryArg) => ({
        url: '/api/v1/tickets/search',
        method: 'POST',
        body: queryArg.ticketSearchRequest,
      }),
      providesTags: () => [{ type: Tags.Ticket, id: 'LIST' }],
    }),
    downloadTicketDocument: build.query<File, DownloadTicketDocumentArg>({
      queryFn: createQueryFn(withOptions(downloadTicketDocument)),
      providesTags: (_result, _error, arg) => [
        { type: Tags.ticket_documents, id: arg.documentId },
      ],
    }),
    downloadWorkflowDocument: build.query<File, DownloadWorkflowDocumentArg>({
      queryFn: createQueryFn(withOptions(downloadWorkflowDocument)),
    }),
    getExpressionAstSchema: build.query<
      GetExpressionAstSchemaApiResponse,
      GetExpressionAstSchemaApiArg
    >({
      query: () => ({ url: '/api/v1/schemas/expression-ast' }),
      // The schema only changes on backend deployment; keep it cached for the
      // lifetime of the session so panel open/close never triggers a refetch.
      keepUnusedDataFor: Infinity,
    }),
  }),
  overrideExisting: false,
});
